import { useDndMonitor, useDraggable } from '@dnd-kit/core';
import { ChevronDown, Columns3, Compass, FormInput, Hash, Image, LineChart, ListOrdered, MapPin, MoveHorizontal, Palette, Puzzle, RotateCw, Scale, SquareCheck, SquareChevronDown, SquareMenu, TextCursor, Volume2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FieldReference from './field-reference';


import type { BlockType } from '@/types/experience';
import type { ComponentType } from 'react';

import { ChoiceTile } from '@/components/ui/choice-tile';
import { useFieldAudit } from '@/hooks/use-field-audit';

export type { BlockType };

export function BlocksIcon({ className }: { className?: string }) {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor' className={className}>
      <rect x='2' y='13' width='9' height='9' rx='1.5' />
      <rect x='13' y='13' width='9' height='9' rx='1.5' />
      <rect x='7.5' y='2' width='9' height='9' rx='1.5' />
    </svg>
  );
}

/**
 * The protractor glyph, which now belongs to TILT rather than to the block it was drawn for.
 *
 * It shows an angle subtended from a baseline — a pose against the horizontal, which is exactly
 * what Tilt measures and exactly what Rotation does not. Rotation took `RotateCw` instead,
 * because a swept path through several turns has no baseline to subtend from.
 *
 * Rotation's own icon is below, and is the same glyph as its library tile — the two were briefly
 * allowed to differ, which is the sort of thing nobody notices and everybody half-registers.
 */
export function TiltIcon({ className }: { className?: string }) {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2} strokeLinecap='round' strokeLinejoin='round' className={className}>
      <path d='M21 21 L3 21 L20 4' />
      <path d='M10 21 A7 7 0 0 0 7.95 16.05' />
    </svg>
  );
}

/** Rotation's glyph, named so the block can import it without knowing it is a lucide icon. */
export function RotationIcon({ className }: { className?: string }) {
  return <RotateCw className={className} />;
}

interface BlockItem {
  type: BlockType;
  labelKey: string;
  Icon: ComponentType<{ className?: string }>;
  disabled?: boolean;
}

/**
 * Block types that exist in the schema but cannot be created.
 *
 * The library still SHOWS them, greyed, so the capability is visible and reactivating one is a
 * flag rather than an archaeology exercise. What makes this a list rather than a `disabled: true`
 * scattered through `SECTIONS` is the cross-block sweeps: the chip, catch-all, participation and
 * predicate-invariant passes all enumerate block types, and a mothballed block must be skipped by
 * DECISION. Omitting it from a list somewhere is how it silently rots and then breaks the next
 * sweep months later.
 *
 * `rotation` is here because its quantity — a swept path with a turn count — has no grading model
 * yet: `RotationTrackerSnapshot.turns` is computed and then discarded, and the domain is clamped to
 * [0, 360] by a `Math.abs` and a `norm360` that a real rotation block would not have. It comes
 * back when that is built.
 */
export const MOTHBALLED_BLOCK_TYPES: BlockType[] = ['rotation'];

export const AVAILABLE_BLOCK_TYPES: BlockType[] = ['text', 'image', 'mcq', 'numerical', 'keyword', 'fitb', 'dropdown', 'slider', 'sound', 'dataviz', 'gps', 'ordering', 'sort', 'scale', 'compass', 'rotation', 'tilt'];

/**
 * The blocks a student ANSWERS, as opposed to the ones they read or watch.
 *
 * Preview uses this to decide what gets a container: a task is a stream of prose, media and
 * things-to-do, and only the last kind has a result to report. Giving every block the same box
 * would turn a page into a form and imply the text and the image were also asking for something.
 *
 * Equivalent to "has a feedback model" — every type here renders fired feedback after Submit, and
 * no type outside it does.
 */
export const ANSWERABLE_BLOCK_TYPES: BlockType[] = ['mcq', 'numerical', 'keyword', 'fitb', 'dropdown', 'slider', 'ordering', 'sort', 'scale', 'gps', 'compass', 'rotation', 'tilt'];

/**
 * Answerable blocks that cannot hold a QUESTION BANK, and so cannot carry a version axis.
 *
 * One entry, and it is the parity debt `project_rotation_parity_debt` records rather than a
 * design choice: nothing calls `addFieldVersion` for Rotation, as `rotation.ts` says in so many
 * words. It has a scenario projection and no versions.
 *
 * Written as the EXCLUSION so that `VERSIONED_BLOCK_TYPES` below is derived. A second hand-kept
 * list would make a new block versioned only if someone remembered to add it, where this makes
 * it versioned by default and fails loudly in `driven-versions-contract` if it is not.
 */
const UNBANKED_BLOCK_TYPES: BlockType[] = ['rotation'];

/** Where a question bank, and therefore a driven version axis, can live. */
export const VERSIONED_BLOCK_TYPES: BlockType[] =
  ANSWERABLE_BLOCK_TYPES.filter(t => !UNBANKED_BLOCK_TYPES.includes(t));

export const BLOCK_META: Record<string, { labelKey: string; shortLabelKey: string; Icon: ComponentType<{ className?: string }> }> = {
  text:      { labelKey: 'task.library.types.text',      shortLabelKey: 'task.library.shortTypes.text',      Icon: SquareMenu },
  image:     { labelKey: 'task.library.types.image',     shortLabelKey: 'task.library.shortTypes.image',     Icon: Image },
  mcq:       { labelKey: 'task.library.types.mcq',       shortLabelKey: 'task.library.shortTypes.mcq',       Icon: SquareCheck },
  numerical: { labelKey: 'task.library.types.numerical', shortLabelKey: 'task.library.shortTypes.numerical', Icon: Hash },
  keyword:   { labelKey: 'task.library.types.keyword',   shortLabelKey: 'task.library.shortTypes.keyword',   Icon: TextCursor },
  fitb:      { labelKey: 'task.library.types.fillBlank', shortLabelKey: 'task.library.shortTypes.fillBlank', Icon: FormInput },
  dropdown:  { labelKey: 'task.library.types.dropdown',  shortLabelKey: 'task.library.shortTypes.dropdown',  Icon: SquareChevronDown },
  slider:    { labelKey: 'task.library.types.slider',    shortLabelKey: 'task.library.shortTypes.slider',    Icon: MoveHorizontal },
  ordering:  { labelKey: 'task.library.types.ordering',  shortLabelKey: 'task.library.shortTypes.ordering',  Icon: ListOrdered },
  sort:      { labelKey: 'task.library.types.sort',      shortLabelKey: 'task.library.shortTypes.sort',      Icon: Columns3 },
  scale:     { labelKey: 'task.library.types.scale',     shortLabelKey: 'task.library.shortTypes.scale',     Icon: Scale },
  sound:     { labelKey: 'task.library.types.sound',     shortLabelKey: 'task.library.shortTypes.sound',     Icon: Volume2 },
  dataviz:   { labelKey: 'task.library.types.dataviz',   shortLabelKey: 'task.library.shortTypes.dataviz',   Icon: LineChart },
  widget:    { labelKey: 'task.library.types.widget',    shortLabelKey: 'task.library.shortTypes.widget',    Icon: Puzzle },
  gps:       { labelKey: 'task.library.types.gps',       shortLabelKey: 'task.library.shortTypes.gps',       Icon: MapPin },
  compass:   { labelKey: 'task.library.types.compass',   shortLabelKey: 'task.library.shortTypes.compass',   Icon: Compass },
  rotation:  { labelKey: 'task.library.types.rotation',  shortLabelKey: 'task.library.shortTypes.rotation',  Icon: RotateCw },
  tilt:      { labelKey: 'task.library.types.tilt',      shortLabelKey: 'task.library.shortTypes.tilt',      Icon: TiltIcon },
};

const SECTIONS: { sectionKey: string; items: BlockItem[] }[] = [
  {
    sectionKey: 'task.library.categories.display',
    items: [
      { type: 'text',  labelKey: 'task.library.types.text',  Icon: SquareMenu },
      { type: 'image', labelKey: 'task.library.types.image', Icon: Image },
      { type: 'sound', labelKey: 'task.library.types.sound', Icon: Volume2 },
      { type: 'dataviz', labelKey: 'task.library.types.dataviz', Icon: LineChart },
    ],
  },
  {
    sectionKey: 'task.library.categories.input',
    items: [
      { type: 'mcq',       labelKey: 'task.library.types.mcq',        Icon: SquareCheck },
      { type: 'numerical', labelKey: 'task.library.types.numerical',   Icon: Hash },
      { type: 'keyword',   labelKey: 'task.library.types.keyword',     Icon: TextCursor },
      { type: 'fitb',      labelKey: 'task.library.types.fillBlank',   Icon: FormInput },
      { type: 'dropdown',  labelKey: 'task.library.types.dropdown',    Icon: SquareChevronDown },
      { type: 'slider',    labelKey: 'task.library.types.slider',      Icon: MoveHorizontal },
      { type: 'ordering',  labelKey: 'task.library.types.ordering',    Icon: ListOrdered },
      // Beside Ordering, which is the family: a permutation next to a partition. `Boxes` rather
      // than a grid or columns glyph, because the bins are containers and the layout they are drawn
      // in is a per-block setting — an icon implying columns would be wrong half the time.
      { type: 'sort',      labelKey: 'task.library.types.sort',        Icon: Columns3 },
      { type: 'scale',     labelKey: 'task.library.types.scale',       Icon: Scale },
    ],
  },
  {
    sectionKey: 'task.library.categories.sensors',
    items: [
      // The grid is `grid-cols-3`, so this order is also the layout: the three live sensors fill
      // the first row and `rotation` sits alone on the second, where a greyed tile reads as
      // "not yet" rather than as a gap in the row above.
      { type: 'gps',      labelKey: 'task.library.types.gps',      Icon: MapPin },
      { type: 'compass',  labelKey: 'task.library.types.compass',  Icon: Compass },
      { type: 'tilt',     labelKey: 'task.library.types.tilt',     Icon: TiltIcon },
      { type: 'rotation', labelKey: 'task.library.types.rotation', Icon: RotateCw,  disabled: true },
    ],
  },
  {
    sectionKey: 'task.library.categories.interactive',
    items: [
      { type: 'widget', labelKey: 'task.library.types.widget', Icon: Puzzle, disabled: true },
    ],
  },
];

interface DraggableItemProps {
  type: BlockType;
  labelKey: string;
  Icon: ComponentType<{ className?: string }>;
  disabled?: boolean;
  onAdd: (type: BlockType) => void;
}

function DraggableLibraryItem({ type, labelKey, Icon, disabled, onAdd }: DraggableItemProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `library-${type}`,
    data: { source: 'library', blockType: type },
    disabled,
  });
  // dragHappenedRef guards onClick for mouse drags (touch handled separately via distance)
  const dragHappenedRef = useRef(false);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  useDndMonitor({
    onDragStart:  () => { dragHappenedRef.current = true; },
    onDragEnd:    () => { setTimeout(() => { dragHappenedRef.current = false; }, 100); },
    onDragCancel: () => { setTimeout(() => { dragHappenedRef.current = false; }, 100); },
  });
  return (
    <li className='relative group'>
      <ChoiceTile
        ref={setNodeRef}
        fit='rail'
        surface='elevated'
        icon={<Icon className='shrink-0' />}
        label={t(labelKey)}
        disabled={disabled}
        title={disabled ? t('task.library.types.comingSoon') : undefined}
        onPointerDown={(e) => { touchStartPos.current = { x: e.clientX, y: e.clientY }; }}
        onClick={() => {
          if (dragHappenedRef.current) return;
          onAdd(type);
        }}
        onTouchEnd={(e) => {
          e.preventDefault(); // suppress synthetic click; dnd-kit still receives touchend via document listener
          const touch = e.changedTouches[0];
          const start = touchStartPos.current;
          touchStartPos.current = null;
          if (!start || !touch) return;
          // distance-based: tap = small movement regardless of duration; drag = finger traveled far
          const dist = Math.hypot(touch.clientX - start.x, touch.clientY - start.y);
          if (dist < 10) onAdd(type);
        }}
        // Only what is specific to a DRAGGABLE tile: the shape is `ChoiceTile`, shared with the
        // image block's empty state, which had grown the same markup on its own.
        className={`touch-none ${disabled ? '' : `cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-50' : 'opacity-100'}`}`}
        {...(!disabled ? { ...attributes, ...listeners } : {})}
      />
      {disabled && (
        <div className='pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md border border-ws-border bg-ws-elevated px-2 py-1 text-xs text-ws-muted shadow-md opacity-0 group-hover:opacity-100 transition-opacity'>
          {t('task.library.types.comingSoon')}
        </div>
      )}
    </li>
  );
}

interface BlockLibraryProps {
  onAdd: (type: BlockType) => void;
  onClose?: () => void;
  isDraggable?: boolean;
}

function useIsMobilePortrait() {
  const mql = '(max-width: 639px) and (orientation: portrait)';
  const [is, setIs] = useState(() => typeof window !== 'undefined' ? window.matchMedia(mql).matches : false);
  useEffect(() => {
    const mq = window.matchMedia(mql);
    const handler = (e: MediaQueryListEvent) => setIs(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return is;
}

function useIsPointerFine() {
  const mql = '(hover: hover) and (pointer: fine)';
  const [is, setIs] = useState(() => typeof window !== 'undefined' ? window.matchMedia(mql).matches : false);
  useEffect(() => {
    const mq = window.matchMedia(mql);
    const handler = (e: MediaQueryListEvent) => setIs(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return is;
}

export default function BlockLibrary({ onAdd, onClose, isDraggable = false }: BlockLibraryProps) {
  const { t } = useTranslation();
  const isMobilePortrait = useIsMobilePortrait();
  const isPointerFine = useIsPointerFine();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const { fieldAudit } = useFieldAudit();
  const [showReference, setShowReference] = useState(false);

  function toggleSection(sectionKey: string) {
    setCollapsed((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  }

  return (
    <aside className={`flex h-full w-full shrink-0 flex-col bg-ws-sidebar ${onClose ? '' : 'border-r border-ws-border'}`}>

      <div className='flex h-14 shrink-0 items-center justify-between gap-2 border-b border-ws-border px-3'>
        <div className='flex items-start gap-2.5'>
          <div className='flex h-10 w-10 shrink-0 self-center items-center justify-center rounded bg-ws-primary'>
            {showReference
              ? <Palette aria-hidden='true' className='h-6 w-6 text-white' />
              : <BlocksIcon aria-hidden='true' className='h-6 w-6 text-white' />}
          </div>
          <div className='flex-1'>
            <span className='text-base font-semibold text-ws-text'>
              {showReference ? t('task.library.fieldReference') : t('task.library.title')}
            </span>
            <p className='text-xs text-ws-muted'>
              {showReference
                ? t('task.library.fieldReferenceHint')
                : isMobilePortrait
                ? t('task.library.tapHint')
                : isPointerFine
                ? t('task.library.dragHint')
                : t('task.library.dragTapHint')}
            </p>
          </div>
        </div>
        {/* Only offered while field tints are on. The reference explains the colours, so with the
            colours off it is a panel about nothing — and the library must not lose space to a
            debug affordance during ordinary authoring. */}
        {fieldAudit && (
          <button
            type='button'
            onClick={() => setShowReference(r => !r)}
            aria-pressed={showReference}
            aria-label={t('task.library.fieldReference')}
            title={t('task.library.fieldReference')}
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${
              showReference ? 'bg-ws-btn-active text-ws-btn-active-fg' : 'text-ws-muted hover:bg-ws-hover hover:text-ws-text'
            }`}
          >
            <Palette aria-hidden='true' className='h-4 w-4' />
          </button>
        )}
      </div>

      {showReference && <FieldReference />}
      {!showReference && (

      <div className='flex-1 overflow-y-auto py-2'>
        {SECTIONS.map(({ sectionKey, items }, idx) => (
          <div key={sectionKey} className={`mb-1 ${idx > 0 ? 'border-t border-ws-border mt-2 pt-1' : ''}`}>
            {onClose ? (
              <p className='px-3 py-2 text-base font-semibold text-ws-text'>{t(sectionKey)}</p>
            ) : (
              <button
                onClick={() => toggleSection(sectionKey)}
                aria-expanded={!collapsed[sectionKey]}
                className='flex w-full items-center justify-between px-3 py-2 text-base font-semibold text-ws-text hover:text-ws-btn-active-fg [&:focus:not(:focus-visible)]:outline-none focus-visible:[outline-offset:-3px]'
              >
                {t(sectionKey)}
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${collapsed[sectionKey] ? '-rotate-90' : ''}`}
                />
              </button>
            )}

            {(!collapsed[sectionKey] || onClose) && (
              <ul role='list' className='grid grid-cols-3 gap-1.5 px-3 pt-1 pb-3'>
                {items.map(({ type, labelKey, Icon, disabled }) =>
                  isDraggable ? (
                    <DraggableLibraryItem key={type} type={type} labelKey={labelKey} Icon={Icon} disabled={disabled} onAdd={onAdd} />
                  ) : (
                    <li key={type} className='relative group'>
                      <button
                        disabled={disabled}
                        title={disabled ? t('task.library.types.comingSoon') : undefined}
                        onClick={() => onAdd(type)}
                        className={`flex w-full flex-col items-center gap-1.5 rounded-lg border px-2 py-3 transition-colors duration-300 ${
                          disabled
                            ? 'cursor-not-allowed border-ws-border bg-ws-disabled-bg text-ws-text-disabled'
                            : 'cursor-pointer text-ws-text hover:bg-ws-card-hover hover:text-ws-btn-active-fg border-ws-border bg-ws-elevated'
                        }`}
                      >
                        <Icon className='h-5 w-5 shrink-0' />
                        <span className='text-center text-xs leading-tight'>{t(labelKey)}</span>
                      </button>
                      {disabled && (
                        <div className='pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md border border-ws-border bg-ws-elevated px-2 py-1 text-xs text-ws-muted shadow-md opacity-0 group-hover:opacity-100 transition-opacity'>
                          {t('task.library.types.comingSoon')}
                        </div>
                      )}
                    </li>
                  )
                )}
              </ul>
            )}
          </div>
        ))}
      </div>
      )}
    </aside>
  );
}
